/* eslint-disable react/jsx-props-no-spreading, react/require-default-props */
import { FC, useMemo, useRef, useState } from 'react'
import { AppContainer } from '@engyalo/delivery-ui-components'
import { selectTreatmentValue } from '@splitsoftware/splitio-redux'
import ModalContainer from '../containers/ModalContainer'
import { getCinnamonPropsContainer, useAppSelector } from '../hooks'
import Dialog from '../components/Dialog'
import { ITemplateDefault } from '../interfaces/templates'
import { useLoadViews } from '../hooks/templates/useLoadViews'
import useTemplateComponentsState from '../hooks/templates/useTemplateComponentsState'
import { useModalsList } from '../hooks/templates/useModalsList'
import { useUnmountedModalReport } from '../hooks/templates/useUnmountedModalReport'
import Alert from '../components/Alert'
import { SPLIT_IO_FEATURES } from '../consts/splitio'
import EventEmitter from '../classes/EventEmiter'
import EventManager from '../containers/EventManager'

const Default: FC<ITemplateDefault> = ({
  container: unprocessedContainer,
  containerName,
  modals,
}) => {
  const eventEmitter = useRef<EventEmitter>(new EventEmitter())

  const state = useAppSelector((stateRedux) => stateRedux)

  const container = useMemo(
    () =>
      getCinnamonPropsContainer(
        unprocessedContainer,
        state,
        eventEmitter.current,
      ),
    [unprocessedContainer, state, eventEmitter],
  )

  const {
    uid: containerUid,
    header = {
      props: {},
      components: [],
    },
    body = {
      props: {},
      components: [],
    },
    footer = {
      props: {},
      components: [],
    },
    modals: modalsNames,
  } = unprocessedContainer

  const [isScriptInjected, setIsScriptInjected] = useState(false)
  const scriptUrl =
    'https://cdn.transcend.io/cm/8c5cabbe-7657-4cbd-9da0-f543ddbc88a8/airgap.js'
  const {
    defaultSlice: { modals: modalStatus, excludedUiComponents },
    splitReducer: splitio,
  } = state

  const userConsentTreatment = selectTreatmentValue(
    splitio,
    SPLIT_IO_FEATURES.ENABLE_USER_CONSENT,
  )

  const isScriptAlreadyInjected = () =>
    document.querySelectorAll(`[src="${scriptUrl}"]`).length > 0

  const addConcentScript = () => {
    if (!isScriptAlreadyInjected()) {
      const script = document.createElement('script')
      script.setAttribute('data-cfasync', 'false')
      script.src = scriptUrl
      document.head.appendChild(script)
      setIsScriptInjected(true)
    }
  }

  if (userConsentTreatment === 'on' && !isScriptInjected) {
    addConcentScript()
  }

  const modalsList = useModalsList(modalsNames, modals)

  const mountedModalNames = useMemo(
    () => modalsList.map(({ name }) => name),
    [modalsList],
  )

  useUnmountedModalReport({
    mountedNames: mountedModalNames,
    containerUid,
    containerName,
    listedModals: modalsNames,
  })

  const { props: headerProps, components: headerComponents } = header
  const { props: bodyProps, components: bodyComponents } = body
  const { props: footerProps, components: footerComponents } = footer

  const {
    headerComponentsList,
    setHeaderComponentsList,
    bodyComponentsList,
    setBodyComponentsList,
    footerComponentsList,
    setFooterComponentsList,
  } = useTemplateComponentsState()

  useLoadViews({
    headerComponents,
    setHeaderComponentsList,
    bodyComponents,
    setBodyComponentsList,
    footerComponents,
    excludedUiComponents,
    setFooterComponentsList,
    containerUid,
  })

  return (
    <>
      <EventManager eventEmitter={eventEmitter.current} />
      <AppContainer
        header={{
          ...headerProps,
          id: `${containerUid}-header`,
          components: headerComponentsList.map(({ uid, wrapper: Wrapper }) => (
            <Wrapper
              parentUid={containerUid}
              key={uid}
              containerSection={container.header}
            />
          )),
        }}
        body={{
          ...bodyProps,
          id: `${containerUid}-body`,
          components: bodyComponentsList.map(({ uid, wrapper: Wrapper }) => (
            <Wrapper
              parentUid={containerUid}
              key={uid}
              containerSection={container.body}
            />
          )),
        }}
        footer={{
          ...footerProps,
          id: `${containerUid}-footer`,
          components: footerComponentsList.map(({ uid, wrapper: Wrapper }) => (
            <Wrapper
              parentUid={containerUid}
              key={uid}
              containerSection={container.footer}
            />
          )),
        }}
      />
      {modalStatus &&
        modalsList.map(({ name, data }) => (
          <ModalContainer
            modalStatus={modalStatus}
            key={name}
            name={name}
            modal={data}
            eventEmitter={eventEmitter.current}
            state={state}
            containerUid={containerUid}
          />
        ))}
      <Alert />
      <Dialog eventEmitter={eventEmitter.current} state={state} />
    </>
  )
}

export default Default
